import {
  AddCard,
  AttachMoney,
  BarChart,
  Business,
  CampaignRounded,
  Devices,
  FiberManualRecord,
  FormatListBulletedRounded,
  Home,
  ManageAccounts,
  NotificationsRounded,
  Payment,
  Settings,
  SupportRounded,
} from '@mui/icons-material'
import { cloneDeep, intersection, isEmpty } from 'lodash'
import { useLocation } from 'react-router-dom'

import useGetAgentCentralized from '~/features/agents/hooks/useGetAgentCentralized'
import useGetAuthSessionQuery from '~/features/auth/hooks/useGetAuthSessionQuery'
import useIsPartner from '~/features/partners/hooks/useIsPartner'
import { ESettingsStepFriendlyNames } from '~/features/settings/models'
import {
  EAdminPortalClientPath,
  EClientPath,
  EMarketingClientPath,
} from '~/models/api'
import { ECognitoGroups } from '~/models/auth'
import EEnvironmentVariables from '~/models/config'
import rem from '~/utils/ui'

export enum ENavSectionNames {
  assistantSetup = 'Assistant Setup',
  campaigns = 'Campaigns',
  conversations = 'Conversations',
}

export enum ENavItemNames {
  overview = 'Dashboard',
  businessInfo = 'Business Info',
  conversationSettings = 'Conversation Settings',
  liveTransfers = 'Live Transfers',
  channels = 'Channels',
  campaignManager = 'Campaign Manager',
  notifications = 'Notifications & More',
  contacts = 'Contacts',
  analytics = 'Analytics',
  settings = 'Assistant Settings',
  campaigns = 'Campaigns',
  reports = 'Reports',
  billing = 'Billing',
  guidance = 'Setup guidance',
  referrals = 'Referrals program',
  help = 'Help',
  support = 'Support',
  integrations = 'Integrations',
  termsOfServices = 'Terms of services',
  privacyPolicy = 'Privacy Policy',
  referralsTerms = 'Referral Terms of Use',
  account = 'Account',
}

export enum EPartnerAdminNavItemNames {
  portalConfig = 'Portal Config',
  generalSettings = 'General Settings',
  branding = 'Branding',
  userManagement = 'User Management',
  payments = 'Payments',
  reports = 'Reports',
}

const partnerAdminNavItems: TNavItem[] = [
  {
    name: EPartnerAdminNavItemNames.portalConfig,
    icon: <Home sx={{ width: rem(17), height: rem(17) }} />,
    path: EClientPath.partnerAdmin,
    children: [
      {
        name: EPartnerAdminNavItemNames.generalSettings,
        path: EClientPath.partnerGeneralSettings,
      },
      {
        name: EPartnerAdminNavItemNames.branding,
        path: EClientPath.partnerBranding,
      },
    ],
  },
  {
    name: EPartnerAdminNavItemNames.userManagement,
    icon: (
      <FormatListBulletedRounded sx={{ width: rem(17), height: rem(17) }} />
    ),
    path: EClientPath.partnerAdminUserManagement,
  },
  {
    name: EPartnerAdminNavItemNames.payments,
    icon: <AddCard sx={{ width: rem(17), height: rem(17) }} />,
    path: EClientPath.partnerPayments,
  },
  {
    name: EPartnerAdminNavItemNames.reports,
    icon: <BarChart sx={{ width: rem(17), height: rem(17) }} />,
    path: EClientPath.partnerAdminReports,
  },
]

enum EHeyLibbyAdminNavItemNames {
  home = 'Home',
  agents = 'Agents',
  users = 'Users',
  reports = 'Reports',
  referrals = 'Referrals',
  templates = 'Templates',
  partnerManagement = 'Partner Management',
  promptMetadata = 'Prompt Metadata',
}

const heyLibbyAdminNavItems: TNavItem[] = [
  {
    name: EHeyLibbyAdminNavItemNames.home,
    icon: <Home sx={{ width: rem(17), height: rem(17) }} />,
    path: EAdminPortalClientPath.admin,
  },
  {
    name: EHeyLibbyAdminNavItemNames.reports,
    icon: <FiberManualRecord sx={{ width: rem(17), height: rem(17) }} />,
    path: EAdminPortalClientPath.reports,
  },
  {
    name: EHeyLibbyAdminNavItemNames.templates,
    icon: <FiberManualRecord sx={{ width: rem(17), height: rem(17) }} />,
    path: EAdminPortalClientPath.templates,
  },
  {
    name: EHeyLibbyAdminNavItemNames.referrals,
    icon: <FiberManualRecord sx={{ width: rem(17), height: rem(17) }} />,
    path: EAdminPortalClientPath.referrals,
  },
  {
    name: EHeyLibbyAdminNavItemNames.partnerManagement,
    icon: <FiberManualRecord sx={{ width: rem(17), height: rem(17) }} />,
    path: EAdminPortalClientPath.partnerManagement,
  },
  {
    name: EHeyLibbyAdminNavItemNames.promptMetadata,
    icon: <FiberManualRecord sx={{ width: rem(17), height: rem(17) }} />,
    path: EAdminPortalClientPath.promptMetadata,
  },
]

const navItems: TNavItem[] = [
  {
    name: ENavItemNames.overview,
    icon: <Home sx={{ width: rem(17), height: rem(17) }} />,
    path: EClientPath.overview,
  },
  {
    name: ENavSectionNames.assistantSetup,
    isSectionHeader: true,
  },
  {
    name: ENavItemNames.businessInfo,
    icon: <Business sx={{ width: rem(17), height: rem(17) }} />,
    children: [
      {
        name: ESettingsStepFriendlyNames.basicInfo,
        path: EClientPath.settingsBasicInfo,
      },
      {
        name: ESettingsStepFriendlyNames.knowledge,
        path: EClientPath.settingsKnowledge,
      },
    ],
  },
  {
    name: ENavItemNames.conversationSettings,
    icon: <Settings sx={{ width: rem(17), height: rem(17) }} />,
    children: [
      {
        name: ESettingsStepFriendlyNames.convoOptions,
        path: EClientPath.settingsConvoOptions,
      },
      import.meta.env[EEnvironmentVariables.VITE_IS_LIVE_TRANSFERS_ENABLED] ===
      'true'
        ? {
            name: ENavItemNames.liveTransfers,
            path: EClientPath.liveTransfersScenarios,
          }
        : undefined,
      {
        name: ESettingsStepFriendlyNames.escalations,
        path: EClientPath.settingsEscalations,
      },
    ].filter(Boolean) as TNavItemChild[],
  },
  {
    name: ENavItemNames.channels,
    icon: <Devices sx={{ width: rem(17), height: rem(17) }} />,
    children: [
      {
        name: ESettingsStepFriendlyNames.chatbot,
        path: EClientPath.settingsChatbot,
      },
      {
        name: ESettingsStepFriendlyNames.email,
        path: EClientPath.settingsEmail,
      },
      {
        name: ESettingsStepFriendlyNames.phoneText,
        path: EClientPath.settingsPhoneText,
      },
    ],
  },
  {
    name: ENavSectionNames.campaigns,
    isSectionHeader: true,
  },
  {
    name: ENavItemNames.campaignManager,
    icon: <CampaignRounded sx={{ width: rem(17), height: rem(17) }} />,
    path: EClientPath.campaignList,
  },
  {
    name: ENavSectionNames.conversations,
    isSectionHeader: true,
  },
  {
    name: ENavItemNames.notifications,
    icon: <NotificationsRounded sx={{ width: rem(17), height: rem(17) }} />,
    path: EClientPath.settingsNotifications,
  },
  {
    name: ENavItemNames.contacts,
    icon: (
      <FormatListBulletedRounded sx={{ width: rem(17), height: rem(17) }} />
    ),
    path: EClientPath.contacts,
  },
  {
    name: ENavItemNames.analytics,
    icon: <BarChart sx={{ width: rem(17), height: rem(17) }} />,
    path: EClientPath.reports,
  },
  {
    name: ENavItemNames.account,
    icon: <ManageAccounts sx={{ width: rem(17), height: rem(17) }} />,
    path: EClientPath.account,
  },
  {
    name: ENavItemNames.billing,
    icon: <Payment sx={{ width: rem(17), height: rem(17) }} />,
    path: EClientPath.billing,
  },
  {
    name: ENavItemNames.referrals,
    icon: (
      <AttachMoney
        sx={{ width: rem(23), height: rem(23), marginLeft: rem(-3) }}
      />
    ),
    path: EClientPath.referralsInfo,
    isOnBottom: true,
  },
  {
    name: ENavItemNames.help,
    icon: <SupportRounded sx={{ width: rem(17), height: rem(17) }} />,
    isOnBottom: true,
    children: [
      {
        name: ENavItemNames.support,
        path: EClientPath.support,
      },
      {
        name: ENavItemNames.integrations,
        path: EClientPath.integrations,
      },
      {
        name: ENavItemNames.termsOfServices,
        path: EClientPath.termsOfUse,
      },
      {
        name: ENavItemNames.privacyPolicy,
        path: EClientPath.privacyPolicy,
      },
      {
        name: ENavItemNames.referralsTerms,
        path: EClientPath.referralsTerms,
      },
    ],
  },
].filter(Boolean) as TNavItem[]

export type TNavItem = {
  name: string
  icon?: JSX.Element
  path?: EClientPath | EAdminPortalClientPath | EMarketingClientPath
  isOnBottom?: boolean
  children?: TNavItemChild[]
  secondaryText?: string
  isSectionHeader?: boolean
}

export type TNavItemChild = {
  name: ENavItemNames | EPartnerAdminNavItemNames | ESettingsStepFriendlyNames
  path?: EClientPath
}

const filterNavItemChildren = (
  children: TNavItemChild[],
  namesToRemove: (
    | ESettingsStepFriendlyNames
    | ENavItemNames
    | EPartnerAdminNavItemNames
  )[],
): TNavItemChild[] => {
  return children.filter((child) => {
    const name = 'name' in child ? child.name : null
    return name && !namesToRemove.includes(name)
  })
}

const filterNavItems = (
  data: TNavItem[],
  namesToRemove: (ESettingsStepFriendlyNames | ENavItemNames)[],
): TNavItem[] => {
  return data.reduce((filteredList: TNavItem[], item: TNavItem) => {
    if (
      !namesToRemove.includes(
        item.name as ESettingsStepFriendlyNames | ENavItemNames,
      )
    ) {
      const newItem = cloneDeep(item)
      if (newItem.children) {
        newItem.children = filterNavItemChildren(
          newItem.children,
          namesToRemove,
        )
      }
      filteredList.push(newItem)
    }
    return filteredList
  }, [])
}
export const useNavItems = () => {
  const isPartner = useIsPartner()
  const { selectedAgent } = useGetAgentCentralized()
  const location = useLocation()
  const { data: authSession } = useGetAuthSessionQuery()
  const userGroups = authSession?.groups || []

  const shouldDisplayPartnerAdminNavItems = location.pathname.includes(
    EClientPath.partnerAdmin,
  )
  const shouldDisplayHeyLibbyAdminNavItems = location.pathname.includes(
    EAdminPortalClientPath.admin,
  )

  const navItemNamesHiddenForPartners: Array<
    ENavItemNames | ESettingsStepFriendlyNames
  > = [
    ENavItemNames.account,
    ENavItemNames.billing,
    ENavItemNames.referrals,
    ENavItemNames.referralsTerms,
  ]

  const hiddenNavItemsForHeyLibby: Array<
    ENavItemNames | ESettingsStepFriendlyNames
  > = [ENavItemNames.billing, ENavItemNames.account]
  const hiddenNavItemsForPartnerEditor = [
    EPartnerAdminNavItemNames.portalConfig,
  ]

  // Add escalations to hidden nav items if the agent has no escalations
  if (isEmpty(selectedAgent?.escalations)) {
    if (isPartner) {
      navItemNamesHiddenForPartners.push(ESettingsStepFriendlyNames.escalations)
    } else {
      hiddenNavItemsForHeyLibby.push(ESettingsStepFriendlyNames.escalations)
    }
  }

  if (shouldDisplayPartnerAdminNavItems) {
    const isUserGroupsUnknown = !userGroups?.length
    const userGroupsAllowedToSeeAllPartnerAdminNavItems = [
      ECognitoGroups.partnerAdmin,
      ECognitoGroups.admin,
    ]
    const isAllowedToSeeAllPartnerAdminItems = isUserGroupsUnknown
      ? false
      : intersection(userGroups, userGroupsAllowedToSeeAllPartnerAdminNavItems)
          .length > 0

    if (isAllowedToSeeAllPartnerAdminItems) {
      return partnerAdminNavItems
    }

    return partnerAdminNavItems.filter(
      (navItem) =>
        !hiddenNavItemsForPartnerEditor.includes(
          navItem.name as EPartnerAdminNavItemNames,
        ),
    )
  }

  if (shouldDisplayHeyLibbyAdminNavItems) {
    return heyLibbyAdminNavItems
  }

  return isPartner
    ? filterNavItems(navItems, navItemNamesHiddenForPartners)
    : filterNavItems(navItems, hiddenNavItemsForHeyLibby)
}

export default useNavItems
